/**
 * Carte unifiée séance/modèle.
 * Dérive ses actions de is_template + user_id selon la matrice :
 *   système  (is_template=true,  user_id=null) → Aperçu + Charger
 *   perso    (is_template=false, user_id=X)    → Voir + Dupliquer + Supprimer
 *   hybride  (is_template=true,  user_id=X)    → Aperçu + Charger + Dupliquer + Supprimer
 */
import { useNavigate } from 'react-router-dom'
import { ChevronRight, Eye, Copy, Trash2 } from 'lucide-react'
import { T } from '../../theme'
import { btnPrimary, btnGhost } from './_shared'

const THEME_LABELS = {
  pressing:       'Pressing',
  construction:   'Construction',
  transition_off: 'Transition off',
  transition_def: 'Transition déf',
  entre_lignes:   'Entre les lignes',
  cote_fort:      'Côté fort',
  finition:       'Finition',
  bloc:           'Bloc',
  cpa_off:        'CPA',
  physique:       'Physique',
}

const STATUS_META = {
  draft:     { label: 'Brouillon', bg: '#9ca3af' },
  planned:   { label: 'Planifiée', bg: T.blue },
  completed: { label: 'Terminée',  bg: T.green },
}


export default function SeanceCard({
  session,
  currentUserId,
  onLoad,
  onDuplicate,
  onDelete,
  loadingCreate,
  loadingDuplicate,
}) {
  const navigate = useNavigate()

  const isTemplate = session.is_template
  const isSystem   = isTemplate && session.user_id === null
  const isOwner    = session.user_id === currentUserId

  const showApercu    = isTemplate
  const showVoir      = !isTemplate
  const showCharger   = isTemplate
  const showDuplicate = !isSystem
  const showDelete    = !isSystem && isOwner

  const themeKeys   = session.theme_keys || []
  const blocksCount = session.blocks_count || 0
  const blockWarn   = blocksCount < 5
  const status      = STATUS_META[session.status] || STATUS_META.draft
  const displayName = isTemplate
    ? (session.template_name || session.name)
    : session.name

  const dateStr = session.date
    ? new Date(session.date).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : null

  return (
    <div style={{
      background: T.surface,
      border: `2px solid ${T.dark}`,
      boxShadow: `4px 4px 0 ${T.dark}`,
      padding: '16px 18px',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      {/* Badges ligne 1 */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {isSystem && (
          <span style={{
            padding: '2px 7px',
            background: T.dark, color: T.gold,
            fontFamily: T.mono, fontSize: 8, letterSpacing: '.18em',
            textTransform: 'uppercase', fontWeight: 700,
          }}>
            Modèle Insightball
          </span>
        )}
        {isTemplate && !isSystem && (
          <span style={{
            padding: '2px 7px',
            background: T.bgAlt, color: T.ink,
            border: `1px solid ${T.dark}`,
            fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em',
            textTransform: 'uppercase', fontWeight: 700,
          }}>
            Modèle perso
          </span>
        )}
        {!isTemplate && (
          <span style={{
            padding: '2px 7px',
            background: status.bg, color: '#fff',
            fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em',
            textTransform: 'uppercase', fontWeight: 700,
          }}>
            {status.label}
          </span>
        )}
        {themeKeys.length === 0 ? (
          <span style={{
            padding: '2px 7px',
            background: T.bgAlt, color: T.muted2,
            border: `1px solid ${T.rule}`,
            fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em',
            textTransform: 'uppercase',
          }}>
            Hors thème
          </span>
        ) : themeKeys.slice(0, 2).map(k => (
          <span key={k} style={{
            padding: '2px 7px',
            background: T.gold, color: T.dark,
            fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em',
            textTransform: 'uppercase', fontWeight: 700,
          }}>
            {THEME_LABELS[k] || k}
          </span>
        ))}
      </div>

      {/* Titre */}
      <h3 style={{
        fontFamily: T.display, fontSize: 18, color: T.ink,
        textTransform: 'uppercase', letterSpacing: '.01em',
        lineHeight: 1.1, margin: 0,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {displayName}
      </h3>

      {/* Meta */}
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap',
        fontFamily: T.mono, fontSize: 10, color: T.muted2, letterSpacing: '.04em',
      }}>
        <span style={{ color: blockWarn ? '#f97316' : T.muted2 }}>
          {blocksCount} procédé{blocksCount > 1 ? 's' : ''}{blockWarn ? ' ⚠' : ''}
        </span>
        {session.number_players_expected && (
          <span>{session.number_players_expected} joueurs</span>
        )}
        {session.total_duration_minutes && (
          <span>{session.total_duration_minutes} min</span>
        )}
        {dateStr && <span>{dateStr}</span>}
      </div>

      {/* Actions */}
      <div
        style={{
          display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 'auto',
          paddingTop: 10, borderTop: `1px solid ${T.rule}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {showApercu && (
          <button
            type="button"
            onClick={() => navigate(`/dashboard/seances/${session.id}`)}
            style={btnGhost()}
          >
            <Eye size={11} /> Aperçu
          </button>
        )}
        {showVoir && (
          <button
            type="button"
            onClick={() => navigate(`/dashboard/seances/${session.id}`)}
            style={btnGhost()}
          >
            <Eye size={11} /> Voir
          </button>
        )}
        {showCharger && (
          <button
            type="button"
            onClick={() => onLoad(session)}
            disabled={loadingCreate}
            style={{
              ...btnPrimary({ disabled: loadingCreate }),
              flex: showDuplicate ? undefined : 1,
              justifyContent: 'center',
            }}
          >
            Charger <ChevronRight size={12} />
          </button>
        )}
        {showDuplicate && (
          <button
            type="button"
            onClick={() => onDuplicate(session)}
            disabled={loadingDuplicate}
            style={btnGhost({ disabled: loadingDuplicate })}
          >
            <Copy size={11} /> Dupliquer
          </button>
        )}
        {showDelete && (
          <button
            type="button"
            onClick={() => onDelete(session)}
            style={btnGhost({ danger: true })}
          >
            <Trash2 size={11} /> Supprimer
          </button>
        )}
      </div>
    </div>
  )
}
